import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import RadioButton, { RadioButtonGroup } from 'carbon-react/lib/components/radio-button';
import { FilterableSelect, Option } from 'carbon-react/lib/components/select';
import Typography from 'carbon-react/lib/components/typography';

import { WorkspaceMember } from '@futrli-sdk/people';
import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';

import { ExplicitPermissionLevel } from '../../hooks/use-share-resource-form';
import { ShareResourceUserRow } from '../../components/share-resource-user-row/share-resource-user-row';
import { useShareResourceContext } from '../../context/share-resource-context';

import * as styles from './share-resource-form-wizard-screen.scss';

interface ShareResourceFormWizardScreenProps {
  error?: string;
  onCancel: () => void;
  onSave: () => void;
}

const getMemberName = (members: WorkspaceMember[], userId: string) => {
  const member = members.find(({ id }) => id === userId);

  return member ? `${member.name.first_name} ${member.name.last_name}` : userId;
};

export const ShareResourceFormWizardScreen = ({ error, onCancel, onSave }: ShareResourceFormWizardScreenProps) => {
  const intl = useIntl();
  const { form, view } = useShareResourceContext();
  const { members } = view.state;
  const resourcePermissions = view.state.resourcePermissions;
  const membersById = useMemo(() => new Map(members.map(member => [member.id, member])), [members]);

  if (!resourcePermissions) {
    return null;
  }

  const overrideAccessPermissions = form.permissions.filter(
    permission => permission.user_id !== resourcePermissions.owner_id
  );
  const explicitlyAccessibleUserIds = new Set(overrideAccessPermissions.map(permission => permission.user_id));
  const membersToAdd = members.filter(
    member => member.id !== resourcePermissions.owner_id && !explicitlyAccessibleUserIds.has(member.id)
  );
  const memberPickerKey = membersToAdd.map(member => member.id).join(',');
  const owner = membersById.get(resourcePermissions.owner_id);
  const ownerName = resourcePermissions.owner_name ?? getMemberName(members, resourcePermissions.owner_id);

  return (
    <WizardScreen
      cancelText={intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' })}
      createText={intl.formatMessage({ id: 'common.save', defaultMessage: 'Save' })}
      footerMode={FooterMode.CREATE}
      onCancel={onCancel}
      onCreate={onSave}
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <Box mb={4}>
          <RadioButtonGroup
            id="sharing-default-access"
            legend={intl.formatMessage({ id: 'sharing.default_access', defaultMessage: 'Default access' })}
            legendHint={intl.formatMessage({
              id: 'sharing.default_access_hint',
              defaultMessage: 'Applies to everyone with access to this organisation'
            })}
            name="sharing-default-access"
            onChange={event => form.setDefaultPermission(event.target.value as typeof form.defaultPermission)}
            value={form.defaultPermission}
          >
            <RadioButton
              id="sharing-default-access-none"
              label={intl.formatMessage({ id: 'sharing.none', defaultMessage: 'None' })}
              value="owner-only"
            />
            <RadioButton
              id="sharing-default-access-view"
              label={intl.formatMessage({ id: 'sharing.view_only', defaultMessage: 'View only' })}
              value="view"
            />
            <RadioButton
              id="sharing-default-access-edit"
              label={intl.formatMessage({ id: 'common.edit', defaultMessage: 'Edit' })}
              value="edit"
            />
          </RadioButtonGroup>
        </Box>

        <Box>
          <Typography as="h3" mb={1} variant="strong">
            {intl.formatMessage({ id: 'sharing.users_with_custom_access', defaultMessage: 'Users with custom access' })}
          </Typography>

          <FilterableSelect
            aria-label={intl.formatMessage({
              id: 'sharing.users_with_custom_access',
              defaultMessage: 'Users with custom access'
            })}
            id="sharing-add-person"
            key={memberPickerKey}
            noResultsMessage={intl.formatMessage({ id: 'sharing.no_people_found', defaultMessage: 'No people found' })}
            onChange={event => {
              if ('selectionConfirmed' in event && event.selectionConfirmed) {
                form.addMember(event.target.value as string);
              }
            }}
            placeholder={intl.formatMessage({
              id: 'sharing.search_users',
              defaultMessage: 'Search users in this organisation'
            })}
            value=""
            mb="10px"
          >
            {membersToAdd.map(member => {
              const name = `${member.name.first_name} ${member.name.last_name}`;

              return <Option key={member.id} text={name} value={member.id} />;
            })}
          </FilterableSelect>

          <ul className={styles.list}>
            <ShareResourceUserRow accessLevel="owner" avatar={owner?.avatar} name={ownerName} />

            {overrideAccessPermissions.map(permission => {
              const member = membersById.get(permission.user_id);
              const memberName = member ? `${member.name.first_name} ${member.name.last_name}` : permission.user_id;

              return (
                <ShareResourceUserRow
                  accessLevel={permission.level as ExplicitPermissionLevel}
                  avatar={member?.avatar}
                  key={permission.user_id}
                  name={memberName}
                  onAccessLevelChange={level => form.setMemberPermission(permission.user_id, level)}
                  onRemove={() => form.removeMember(permission.user_id)}
                />
              );
            })}
          </ul>
        </Box>
      </>
    </WizardScreen>
  );
};
